import { useState } from 'react'
import { supabase } from '../lib/supabase'
import { useI18n } from '../i18n/useI18n'
import { Sheet } from '../components/Sheet'
import {
  TextField, TextAreaField, PhoneField, SelectField,
} from '../components/Fields'
import { canDelete } from '../lib/types'
import type { Tenant } from '../lib/types'
import { useAuth } from '../auth/useAuth'
import { ConfirmDelete } from '../components/ConfirmDelete'

type IdType = '' | 'cni' | 'passeport' | 'autre'

/** Creation et modification d'un locataire. */
export function TenantSheet({
  tenant,
  leaseCount = 0,
  onClose,
  onSaved,
  onDeleted,
}: {
  tenant: Tenant | null
  /** Nombre de baux de ce locataire. Zero le rend supprimable. */
  leaseCount?: number
  onClose: () => void
  onSaved: (tenantId: string) => void
  onDeleted?: () => void
}) {
  const { t } = useI18n()
  const { profile } = useAuth()
  const peutSupprimer = canDelete(profile?.role ?? null)

  const [fullName, setFullName] = useState(tenant?.full_name ?? '')
  const [phone, setPhone] = useState(tenant?.phone ?? '')
  const [idType, setIdType] = useState<IdType>((tenant?.id_type as IdType) ?? '')
  const [idNumber, setIdNumber] = useState(tenant?.id_number ?? '')
  const [notes, setNotes] = useState(tenant?.notes ?? '')

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit() {
    setError(null)
    setSubmitting(true)

    const payload = {
      full_name: fullName.trim(),
      phone: phone.trim() || null,
      id_type: idType || null,
      id_number: idNumber.trim() || null,
      notes: notes.trim() || null,
    }

    const { data, error: e } = tenant
      ? await supabase.from('tenants').update(payload).eq('id', tenant.id)
          .select('id').single()
      : await supabase.from('tenants').insert(payload).select('id').single()

    setSubmitting(false)
    if (e) {
      setError(`${t('save_failed')} ${e.message}`)
      return
    }
    onSaved(data.id as string)
  }

  return (
    <Sheet
      title={tenant ? t('tenant_edit') : t('tenant_add')}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={t('save')}
      submitting={submitting}
      disabled={fullName.trim().length === 0}
      error={error}
    >
      <TextField
        label={t('tenant_name')}
        hint={t('tenant_name_hint')}
        required
        value={fullName}
        onChange={setFullName}
      />

      <PhoneField
        label={t('tenant_phone')}
        hint={t('optional')}
        value={phone}
        onChange={setPhone}
      />

      <SelectField<IdType>
        label={t('tenant_id_type')}
        value={idType}
        onChange={setIdType}
        options={[
          { value: '', label: t('id_type_none') },
          { value: 'cni', label: t('id_type_cni') },
          { value: 'passeport', label: t('id_type_passeport') },
          { value: 'autre', label: t('id_type_autre') },
        ]}
      />

      {idType !== '' && (
        <TextField
          label={t('tenant_id_number')}
          value={idNumber}
          onChange={setIdNumber}
          autoCapitalize="none"
        />
      )}

      <TextAreaField
        label={t('tenant_notes')}
        hint={t('optional')}
        value={notes}
        onChange={setNotes}
      />

      {/* Suppression, reservee au proprietaire et aux locataires SANS
          AUCUN BAIL. Un locataire qui porte un bail ne se supprime
          pas : ce bail est l'historique de l'unite. Le declencheur de
          db/009 le refuse aussi cote base, et la cle etrangere
          l'empechait deja. */}
      {tenant && peutSupprimer && (
        <ConfirmDelete
          label={t('tenant_delete')}
          question={t('tenant_delete_confirm', { name: tenant.full_name })}
          disabled={submitting}
          blockedReason={
            leaseCount > 0 ? t('tenant_delete_blocked', { n: leaseCount }) : null
          }
          onDelete={async () => {
            const { error: e } = await supabase
              .from('tenants')
              .delete()
              .eq('id', tenant.id)
            if (e) throw new Error(e.message)
            onDeleted?.()
          }}
        />
      )}
    </Sheet>
  )
}
